// src/pages/AkcniCenyPage.jsx
import React, { useEffect, useState } from "react";
import { Download, Info } from "lucide-react";

export default function AkcniCenyPage() {
  const [blobUrl, setBlobUrl] = useState("");
  const [err, setErr] = useState("");
  const [meta, setMeta] = useState(null);
  const [downloadName, setDownloadName] = useState("akcni.pdf");
  const [expired, setExpired] = useState(false);
  const [applyFilter, setApplyFilter] = useState(false);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()} ${hh}:${mm}:${ss}`;
  };

  useEffect(() => {
    let url = "";

    (async () => {
      try {
        setErr("");

        // 🔹 1) META
        const metaRes = await fetch("/api/reports/akcni/meta", {
          method: "GET",
          credentials: "include",
        });

        if (!metaRes.ok) throw new Error("Chyba meta");

        const metaJson = await metaRes.json();

        if (!metaJson?.ok) {
          setErr("Nelze načíst metadata akčního letáku.");
          return;
        }

        setMeta(metaJson.meta);

        // 🔹 2) kontrola platnosti
        const validTo = metaJson.meta?.validTo
          ? new Date(metaJson.meta.validTo)
          : null;

        if (validTo && new Date() > validTo) {
          setExpired(true);
          return; // nebudeme načítat PDF
        }

        // 🔹 3) PDF
        const pdfRes = await fetch(
          `/api/reports/akcni/pdf${applyFilter ? "?filter=1" : ""}`,
          { method: "GET", credentials: "include" }
        );

        if (!pdfRes.ok) throw new Error("Chyba PDF");

        const blob = await pdfRes.blob();
        url = URL.createObjectURL(blob);
        setBlobUrl(url);

        // Jméno souboru
        const d = metaJson.meta?.lastUpdated
          ? new Date(metaJson.meta.lastUpdated)
          : new Date();

        if (!isNaN(d)) {
          const y = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, "0");
          const dd = String(d.getDate()).padStart(2, "0");
          setDownloadName(`akcni_${y}_${mm}_${dd}.pdf`);
        }
      } catch (e) {
        console.error(e);
        setErr("Nelze načíst PDF Akčního letáku.");
      }
    })();

    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [applyFilter]);

  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-green-100">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-green-900 flex items-center gap-4">
          Akční ceny
          {meta?.validFrom && meta?.validTo && (
            <span className="text-sm font-normal text-gray-600">
              Platnost: {formatDate(meta.validFrom)} – {formatDate(meta.validTo)}
            </span>
          )}
          {meta?.lastBatch && (
            <span className="text-sm font-normal text-gray-600">
              | Poslední export: {formatDateTime(meta.lastBatch)}
            </span>
          )}
        </h2>

        {/* Checkbox + tooltip */}
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={applyFilter}
              onChange={(e) => setApplyFilter(e.target.checked)}
              className="w-4 h-4 accent-green-600"
            />
            Zapnout / Vypnout filtr SimplyYou a nízkých pohybů
            <div className="relative group">
              <Info className="w-4 h-4 text-gray-500 hover:text-green-700" />
              <div className="absolute hidden group-hover:block bg-gray-800 text-white text-xs rounded-lg p-2 w-64 -left-40 top-6 z-50 shadow-lg">
                Filtr odstraní sortiment SimplyYou (podle PDK ze CSV) a položky s &lt; 3 kusy za 6&nbsp;měsíců.
              </div>
            </div>
          </label>

          {blobUrl && !expired && (
            <a
              href={blobUrl}
              download={downloadName}
              className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-green-200 hover:bg-green-50"
            >
              <Download className="w-4 h-4" />
              Stáhnout PDF
            </a>
          )}
        </div>
      </div>

      {err ? (
        <div className="p-4 text-red-600">{err}</div>
      ) : expired ? (
        <div className="p-4 text-gray-600">
          Aktuálně není platný žádný akční leták.
        </div>
      ) : !blobUrl ? (
        <div className="p-4">Načítání…</div>
      ) : (
        <div className="rounded-lg overflow-hidden border border-green-100">
          <object
            data={blobUrl}
            type="application/pdf"
            className="w-full"
            style={{ height: "80vh" }}
          >
            <p className="p-4">
              Nelze zobrazit PDF.{" "}
              <a
                href={blobUrl}
                download={downloadName}
                className="text-green-700 underline"
              >
                Stáhnout PDF
              </a>
            </p>
          </object>
        </div>
      )}
    </div>
  );
}
